import { defaultTheme } from 'react-admin';
import type { Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

// Shared with the NLC restoration platform so the two read as one product family.
const primary = '#12617d';
const ground = '#f4f7f9';

export const DRAWER_WIDTH = 264;

// Phone save bars are portalled to the body at this level, since the layout is its own stacking
// context: above the page, under the drawer and dialogs. A dropdown kept off them goes one below.
export const saveBarZIndex = (theme: Theme) => theme.zIndex.appBar;

export const forgeTheme = {
  ...defaultTheme,
  // The drawer never collapses to icons, so react-admin's closed width is the full width.
  sidebar: {
    width: DRAWER_WIDTH,
    closedWidth: DRAWER_WIDTH
  },
  palette: {
    ...defaultTheme.palette,
    mode: 'light' as const,
    primary: {
      main: primary,
      dark: '#123047',
      // A pale tint, not MUI's computed mid-tone: screens use it as a background.
      light: '#e3f0f4',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#ce6a3a'
    },
    // Darker than the MUI defaults so white text on a filled chip meets WCAG AA (4.5:1).
    info: {
      main: '#0277bd'
    },
    warning: {
      main: '#c2410c'
    },
    background: {
      default: ground,
      paper: '#ffffff'
    },
    text: {
      primary: '#172033',
      secondary: '#667085'
    },
    divider: '#e4e9f1',
    DataGrid: {
      bg: '#ffffff',
      headerBg: '#edf2f8',
      pinnedBg: '#f8fafc'
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 750,
      letterSpacing: '-0.025em'
    },
    h2: {
      fontSize: '1.35rem',
      fontWeight: 700
    },
    // Forge's page headings are h4; they get the platform's h1 page-title treatment.
    h4: {
      fontSize: '2rem',
      fontWeight: 750,
      letterSpacing: '-0.025em'
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h6: {
      fontSize: '1.05rem',
      fontWeight: 650
    },
    subtitle1: {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    body1: {
      fontSize: '0.925rem'
    },
    body2: {
      fontSize: '0.875rem'
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 650,
      letterSpacing: 0
    }
  },
  components: {
    ...defaultTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: ground
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        // The ripple was the keyboard focus cue on text and icon buttons, so draw one instead.
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${primary}`,
            outlineOffset: 2
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 36
        }
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          boxShadow: '0 8px 28px rgba(18,48,71,.08)'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: '#dfe5ee',
          paddingTop: 11,
          paddingBottom: 11
        },
        head: {
          color: '#475467',
          fontSize: '0.8125rem',
          fontWeight: 700,
          letterSpacing: '0.01em',
          textTransform: 'none'
        }
      }
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 48,
        columnHeaderHeight: 48
      },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderColor: '#cfd7e5',
          color: '#243044',
          fontSize: '0.925rem',
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#edf2f8',
            color: '#344054',
            borderBottom: '1px solid #cfd7e5'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            letterSpacing: '0.005em'
          },
          '& .MuiDataGrid-cell': {
            borderColor: '#dfe5ee',
            lineHeight: 1.45
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8fafc'
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#eaf3f6'
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#ffffff',
            borderTopColor: '#cfd7e5'
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
            outlineColor: primary,
            outlineOffset: -2
          }
        }
      }
    },
    // A table wider than its card scrolls inside it rather than being cut off on a phone.
    RaDatagrid: {
      styleOverrides: {
        root: {
          '& .RaDatagrid-tableWrapper': { overflowX: 'auto' as const }
        }
      }
    },
    // A tab row wider than a phone scrolls instead of cutting off the last tabs.
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable' as const,
        scrollButtons: 'auto' as const
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          paddingLeft: 12,
          paddingRight: 12,
          textTransform: 'none',
          fontWeight: 600,
          color: '#667085',
          '&.Mui-selected': {
            color: primary
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600
        }
      }
    }
  }
};
